import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarDays, Plus, Trash2, DownloadCloud, Loader2, ChevronRight, Pencil, Check, X } from "lucide-react";
import { orgNowLocal } from "@/lib/tz";

const TEAL = "#2f6f6b";
const REGIONS = [
  { code: "AU-QLD", label: "Queensland (national + QLD)" },
  { code: "AU-NSW", label: "New South Wales" },
  { code: "AU-VIC", label: "Victoria" },
  { code: "AU-ACT", label: "ACT" },
  { code: "AU-NT", label: "Northern Territory" },
  { code: "AU-SA", label: "South Australia" },
  { code: "AU-TAS", label: "Tasmania" },
  { code: "AU-WA", label: "Western Australia" },
  { code: "AU", label: "National holidays only" },
];
// Holiday times are org-local "HH:MM" business times; "24:00" (or a To of 00:00) is midnight.
const fmtHm = (t) => {
  const [h, m] = String(t).split(":").map(Number);
  if (h === 0 || h === 24) return m ? `12:${String(m).padStart(2, "0")} AM` : "midnight";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
};
const fmtWindow = (h) => (!h.start_time ? "" : h.end_time === "24:00" ? `from ${fmtHm(h.start_time)}` : `${fmtHm(h.start_time)} – ${fmtHm(h.end_time)}`);
// Time inputs can't show 24:00, so midnight is edited as 00:00.
const toInput = (t) => (t === "24:00" ? "00:00" : t || "");

// All day, or From/To times (To defaults to midnight).
function TimesFields({ v, onChange, testid }) {
  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="flex items-center gap-1.5 h-8 text-[11px] text-slate-600">
        <Checkbox checked={v.allDay} onCheckedChange={(c) => onChange({ ...v, allDay: !!c })} data-testid={`${testid}-allday`} />All day
      </label>
      {!v.allDay && (
        <>
          <label className="text-[11px] text-slate-500">From<Input type="time" value={v.start} onChange={(e) => onChange({ ...v, start: e.target.value })} className="mt-0.5 h-8 w-28 text-sm" data-testid={`${testid}-from`} /></label>
          <label className="text-[11px] text-slate-500">To <span className="text-slate-400">(00:00 = midnight)</span><Input type="time" value={v.end} onChange={(e) => onChange({ ...v, end: e.target.value })} className="mt-0.5 h-8 w-28 text-sm" data-testid={`${testid}-to`} /></label>
        </>
      )}
    </div>
  );
}
const ALL_DAY = { allDay: true, start: "", end: "00:00" };
const timesArgs = (v) => (v.allDay ? [null, null] : [v.start || null, v.end || null]);
const timesError = (v) => (!v.allDay && !v.start ? "Enter a From time, or tick All day." : "");

const fmt = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function HolidaysAdmin() {
  const [lists, setLists] = useState([]);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [newList, setNewList] = useState("");
  const [busy, setBusy] = useState("");
  const [addDate, setAddDate] = useState("");
  const [addDesc, setAddDesc] = useState("");
  const [addTimes, setAddTimes] = useState(ALL_DAY);
  const [edit, setEdit] = useState(null); // { row, desc, times }
  const yr = orgNowLocal().year();
  const [region, setRegion] = useState("AU-QLD");
  const [fromYr, setFromYr] = useState(yr);
  const [toYr, setToYr] = useState(yr + 5);

  const loadLists = () => api.holidayLists().then((d) => setLists(d.lists || [])).catch(() => {});
  const loadDetail = (name) => api.holidayListDetail(name).then(setDetail).catch(() => {});
  useEffect(() => { loadLists(); }, []);
  useEffect(() => { if (sel) loadDetail(sel); }, [sel]);

  const createList = async () => {
    const t = newList.trim();
    if (!t) return;
    try { await api.holidayCreateList(t); setNewList(""); await loadLists(); setSel(t); toast.success("Holiday list created."); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const deleteList = async (name) => {
    if (!window.confirm(`Delete the holiday list "${name}"? Staff assigned to it will lose their public-holiday rates.`)) return;
    try { await api.holidayDeleteList(name); if (sel === name) { setSel(null); setDetail(null); } await loadLists(); toast.success("List deleted."); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const addHoliday = async () => {
    if (!addDate) { toast.error("Pick a date."); return; }
    if (timesError(addTimes)) { toast.error(timesError(addTimes)); return; }
    try { await api.holidayAdd(sel, addDate, addDesc, ...timesArgs(addTimes)); setAddDate(""); setAddDesc(""); setAddTimes(ALL_DAY); await loadDetail(sel); await loadLists(); toast.success("Holiday added."); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const startEdit = (h) => setEdit({ row: h.row, desc: h.description || "", times: h.start_time ? { allDay: false, start: h.start_time, end: toInput(h.end_time) } : ALL_DAY });
  const saveEdit = async () => {
    if (timesError(edit.times)) { toast.error(timesError(edit.times)); return; }
    try { await api.holidayEdit(sel, edit.row, edit.desc, ...timesArgs(edit.times)); setEdit(null); await loadDetail(sel); await loadLists(); toast.success("Holiday updated."); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const delHoliday = async (row) => {
    try { await api.holidayDelete(sel, row); await loadDetail(sel); await loadLists(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  const doImport = async () => {
    setBusy("import");
    try {
      const r = await api.holidayImport(sel, region, Number(fromYr), Number(toYr));
      await loadDetail(sel); await loadLists();
      toast.success(`Added ${r.added} public holiday${r.added === 1 ? "" : "s"} (${r.total} total).`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to fetch."); }
    finally { setBusy(""); }
  };

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="holidays-admin">
      <div className="flex items-center gap-2.5 mb-1"><CalendarDays className="h-5 w-5" style={{ color: TEAL }} /><h1 className="text-xl font-semibold font-brand text-slate-800">Public Holidays</h1></div>
      <p className="text-sm text-slate-500 mb-5">Manage the holiday lists that drive public-holiday pricing. Add or remove dates by hand, or auto-fill a state's statutory holidays for a year range. Regional show days (e.g. the Brisbane Ekka) aren't in the national feed — add those manually.</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Lists column */}
        <div className="space-y-3">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h2 className="text-sm font-semibold font-brand text-slate-800 mb-2">Holiday lists</h2>
            <div className="space-y-1.5">
              {lists.map((l) => (
                <button key={l.name} onClick={() => setSel(l.name)} data-testid={`holiday-list-${l.name}`}
                  className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${sel === l.name ? "border-teal-300 bg-teal-50/60" : "border-slate-200 hover:bg-slate-50"}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">{l.title}</span>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </div>
                  <div className="text-[11px] text-slate-500">{l.count} holidays{l.part_day ? ` (${l.part_day} part-day)` : ""} · {l.from_date ? `${(l.from_date || "").slice(0, 4)}–${(l.to_date || "").slice(0, 4)}` : "empty"}</div>
                </button>
              ))}
              {!lists.length && <p className="text-[11px] text-slate-400">No lists yet.</p>}
            </div>
            <div className="mt-3 flex gap-2">
              <Input value={newList} onChange={(e) => setNewList(e.target.value)} placeholder="New list name" className="h-8 text-sm" data-testid="new-list-input" />
              <Button size="sm" style={{ backgroundColor: TEAL }} onClick={createList} data-testid="create-list-btn"><Plus className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>

        {/* Detail column */}
        <div className="md:col-span-2 space-y-4">
          {!sel && <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-400" data-testid="no-list-selected">Select a holiday list to manage its dates.</div>}
          {sel && detail && (
            <>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold font-brand text-slate-800">{detail.title} <Badge className="ml-1 border bg-slate-100 text-slate-500 border-slate-200">{detail.holidays.length}</Badge></h2>
                  <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={() => deleteList(sel)} data-testid="delete-list-btn"><Trash2 className="h-4 w-4 mr-1" />Delete list</Button>
                </div>
                {/* Auto-pull */}
                <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 mb-3">
                  <div className="text-[11px] font-semibold text-slate-600 mb-2">Auto-fill statutory public holidays</div>
                  <div className="flex flex-wrap items-end gap-2">
                    <label className="text-[11px] text-slate-500">Region
                      <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="region-select" className="mt-0.5 block h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
                        {REGIONS.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                      </select>
                    </label>
                    <label className="text-[11px] text-slate-500">From<Input type="number" value={fromYr} onChange={(e) => setFromYr(e.target.value)} className="mt-0.5 h-8 w-24 text-sm" data-testid="from-year" /></label>
                    <label className="text-[11px] text-slate-500">To<Input type="number" value={toYr} onChange={(e) => setToYr(e.target.value)} className="mt-0.5 h-8 w-24 text-sm" data-testid="to-year" /></label>
                    <Button size="sm" style={{ backgroundColor: TEAL }} disabled={busy === "import"} onClick={doImport} data-testid="import-holidays-btn">
                      {busy === "import" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <DownloadCloud className="h-4 w-4 mr-1.5" />}Fetch
                    </Button>
                  </div>
                  <p className="mt-2 text-[11px] text-slate-500" data-testid="part-day-hint">Auto-fill adds whole days only. Part-day holidays, such as Christmas Eve and New Year's Eve evenings in some states, aren't in the feed: add those below and untick All day.</p>
                </div>
                {/* Add one */}
                <div className="flex flex-wrap items-end gap-2">
                  <label className="text-[11px] text-slate-500">Date<Input type="date" value={addDate} onChange={(e) => setAddDate(e.target.value)} className="mt-0.5 h-8 text-sm" data-testid="add-date" /></label>
                  <label className="text-[11px] text-slate-500 flex-1 min-w-[160px]">Description<Input value={addDesc} onChange={(e) => setAddDesc(e.target.value)} placeholder="e.g. Brisbane Ekka (People's Day)" className="mt-0.5 h-8 text-sm" data-testid="add-desc" /></label>
                  <TimesFields v={addTimes} onChange={setAddTimes} testid="add" />
                  <Button size="sm" variant="outline" onClick={addHoliday} data-testid="add-holiday-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-2" data-testid="holidays-table">
                {detail.holidays.map((h) => edit?.row === h.row ? (
                  <div key={h.row} className="flex flex-wrap items-end gap-2 px-2 py-2 border-b border-slate-100 last:border-0 bg-slate-50/60 text-sm" data-testid={`edit-holiday-${h.row}`}>
                    <span className="w-40 h-8 flex items-center tabular-nums text-slate-600">{fmt(h.holiday_date)}</span>
                    <label className="text-[11px] text-slate-500 flex-1 min-w-[160px]">Description<Input value={edit.desc} onChange={(e) => setEdit({ ...edit, desc: e.target.value })} className="mt-0.5 h-8 text-sm" data-testid="edit-desc" /></label>
                    <TimesFields v={edit.times} onChange={(t) => setEdit({ ...edit, times: t })} testid="edit" />
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-teal-700" onClick={saveEdit} data-testid="save-edit-btn"><Check className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400" onClick={() => setEdit(null)} data-testid="cancel-edit-btn"><X className="h-4 w-4" /></Button>
                  </div>
                ) : (
                  <div key={h.row} className="flex items-center gap-3 px-2 py-1.5 border-b border-slate-100 last:border-0 text-sm">
                    <span className="w-40 tabular-nums text-slate-600">{fmt(h.holiday_date)}</span>
                    <span className="flex-1 text-slate-700">
                      {h.description || "Public Holiday"}
                      {h.start_time && <span className="text-amber-700" data-testid={`holiday-window-${h.row}`}> · {fmtWindow(h)}</span>}
                    </span>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-slate-700" onClick={() => startEdit(h)} data-testid={`edit-holiday-btn-${h.row}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400 hover:text-rose-600" onClick={() => delHoliday(h.row)} data-testid={`del-holiday-${h.row}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
                {!detail.holidays.length && <p className="p-4 text-center text-[11px] text-slate-400">No holidays in this list yet — add some above.</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
